import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  BookOpen, ChevronRight, Plus, X, Sparkles,
  FlaskConical, BarChart2, Layers, RefreshCw, BookMarked, Microscope,
} from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import api from '../../lib/api'

// ─── Methodology options ──────────────────────────────────────────────────────

const METHODOLOGIES = [
  { value: 'Qualitative',     label: 'Qualitative',     icon: BookMarked,  desc: 'Interviews, focus groups, thematic analysis' },
  { value: 'Quantitative',    label: 'Quantitative',    icon: BarChart2,   desc: 'Surveys, experiments, statistical analysis' },
  { value: 'Mixed Methods',   label: 'Mixed Methods',   icon: Layers,      desc: 'Both qualitative and quantitative phases' },
  { value: 'Action Research', label: 'Action Research', icon: RefreshCw,   desc: 'Iterative cycles, practitioner-led' },
  { value: 'Case Study',      label: 'Case Study',      icon: Microscope,  desc: 'In-depth contextual investigation' },
  { value: 'Grounded Theory', label: 'Grounded Theory', icon: FlaskConical, desc: 'Theory emerging from data' },
]

// ─── Dynamic list field ───────────────────────────────────────────────────────

function DynamicList({ label, hint, items, onChange, placeholder }) {
  const add    = () => onChange([...items, ''])
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i))
  const set    = (i, v) => onChange(items.map((item, idx) => idx === i ? v : item))

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="font-sans text-sm font-medium text-acdyon-text">{label}</label>
        <button
          type="button"
          onClick={add}
          className="flex items-center gap-1 font-sans text-xs px-2 py-1 rounded-md transition-colors"
          style={{ color: '#2563eb', background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.2)' }}
        >
          <Plus style={{ width: 11, height: 11 }} /> Add
        </button>
      </div>
      {hint && <p className="font-sans text-xs text-acdyon-textFaint mb-3">{hint}</p>}
      {items.length === 0 ? (
        <button
          type="button"
          onClick={add}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-sans text-sm text-acdyon-textFaint transition-colors hover:text-acdyon-textMuted"
          style={{ border: '1px dashed #DDE4EF' }}
        >
          <Plus style={{ width: 14, height: 14 }} /> Click to add {label.toLowerCase()}
        </button>
      ) : (
        <div className="space-y-2">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="font-mono text-xs shrink-0" style={{ color: '#2563eb', width: 20 }}>{i + 1}.</span>
              <input
                type="text"
                value={item}
                onChange={e => set(i, e.target.value)}
                placeholder={placeholder}
                className="flex-1 px-3 py-2 rounded-lg font-sans text-sm text-acdyon-text placeholder:text-acdyon-textFaint outline-none transition-colors"
                style={{ background: '#FFFFFF', border: '1px solid #C8D4E4' }}
                onFocus={e => (e.target.style.borderColor = '#2563eb')}
                onBlur={e => (e.target.style.borderColor = '#C8D4E4')}
              />
              <button type="button" onClick={() => remove(i)} className="shrink-0 p-1 rounded hover:bg-red-500/10 transition-colors">
                <X style={{ width: 14, height: 14, color: '#4A6080' }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Keyword tag input ────────────────────────────────────────────────────────

function KeywordInput({ keywords, onChange }) {
  const [input, setInput] = useState('')

  const add = () => {
    const v = input.trim()
    if (v && !keywords.includes(v)) onChange([...keywords, v])
    setInput('')
  }

  const remove = (kw) => onChange(keywords.filter(k => k !== kw))

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add() }
    if (e.key === 'Backspace' && !input && keywords.length) remove(keywords[keywords.length - 1])
  }

  return (
    <div>
      <label className="font-sans text-sm font-medium text-acdyon-text block mb-2">Keywords</label>
      <p className="font-sans text-xs text-acdyon-textFaint mb-3">Press Enter or comma to add</p>
      <div
        className="flex flex-wrap gap-2 p-3 rounded-lg min-h-[46px]"
        style={{ background: '#FFFFFF', border: '1px solid #C8D4E4' }}
        onClick={() => document.getElementById('kw-input').focus()}
      >
        {keywords.map(kw => (
          <span
            key={kw}
            className="flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-xs"
            style={{ background: 'rgba(37,99,235,0.12)', color: '#2563eb', border: '1px solid rgba(37,99,235,0.25)' }}
          >
            {kw}
            <button type="button" onClick={() => remove(kw)}><X style={{ width: 10, height: 10 }} /></button>
          </span>
        ))}
        <input
          id="kw-input"
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={add}
          placeholder={keywords.length ? '' : 'Add keywords…'}
          className="bg-transparent font-sans text-sm text-acdyon-text placeholder:text-acdyon-textFaint outline-none min-w-[140px] flex-1"
        />
      </div>
    </div>
  )
}

// ─── AI Title Modal ───────────────────────────────────────────────────────────

function TitleSuggestModal({ open, onClose, onSelect, formData }) {
  const [loading, setLoading] = useState(false)
  const [titles, setTitles] = useState([])
  const [error, setError] = useState('')

  const generate = async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = await api.post('/thesis/ai/suggest-titles', {
        domain:        formData.domain,
        methodology:   formData.methodology,
        keywords:      formData.keywords,
        researchTopic: formData.researchTopic,
        partialTitle:  formData.title,
      })
      setTitles(data.titles || [])
    } catch {
      setError('Failed to generate suggestions. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && titles.length === 0) generate()
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(6,11,20,0.8)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg mx-4 rounded-2xl p-6"
        style={{ background: '#FFFFFF', border: '1px solid #DDE4EF' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Sparkles style={{ width: 16, height: 16, color: '#2563eb' }} />
            <h3 className="font-display text-lg text-acdyon-text">AI Title Suggestions</h3>
          </div>
          <button onClick={onClose}><X style={{ width: 16, height: 16, color: '#8EA4BC' }} /></button>
        </div>

        {loading ? (
          <div className="py-10 text-center">
            <div className="w-8 h-8 rounded-full border-2 border-brand-500 border-t-transparent animate-spin mx-auto mb-3" />
            <p className="font-sans text-sm text-acdyon-textMuted">Generating scholarly titles…</p>
          </div>
        ) : error ? (
          <div className="py-6 text-center">
            <p className="font-sans text-sm text-red-400 mb-4">{error}</p>
            <button
              onClick={generate}
              className="px-4 py-2 rounded-lg font-sans text-sm font-medium"
              style={{ background: '#2563eb', color: '#060B14' }}
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {titles.map((title, i) => (
              <button
                key={i}
                type="button"
                onClick={() => { onSelect(title); onClose() }}
                className="w-full text-left px-4 py-3 rounded-lg font-sans text-sm text-acdyon-text transition-all"
                style={{ background: '#FFFFFF', border: '1px solid #C8D4E4' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(37,99,235,0.4)'; e.currentTarget.style.color = '#2563eb' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#DDE4EF'; e.currentTarget.style.color = '#0C1629' }}
              >
                {title}
              </button>
            ))}
            <button
              type="button"
              onClick={generate}
              className="w-full flex items-center justify-center gap-2 py-2 font-sans text-xs text-acdyon-textMuted hover:text-acdyon-text transition-colors mt-2"
            >
              <RefreshCw style={{ width: 12, height: 12 }} /> Generate more
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Form field component ─────────────────────────────────────────────────────

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="font-sans text-sm font-medium text-acdyon-text block mb-1">{label}</label>
      {hint && <p className="font-sans text-xs text-acdyon-textFaint mb-2">{hint}</p>}
      {children}
    </div>
  )
}

function Input({ value, onChange, placeholder, ...rest }) {
  return (
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 rounded-lg font-sans text-sm text-acdyon-text placeholder:text-acdyon-textFaint outline-none transition-colors"
      style={{ background: '#FFFFFF', border: '1px solid #C8D4E4' }}
      onFocus={e => (e.target.style.borderColor = '#2563eb')}
      onBlur={e => (e.target.style.borderColor = '#C8D4E4')}
      {...rest}
    />
  )
}

// ─── Existing Thesis Card ─────────────────────────────────────────────────────

function ExistingThesisCard({ thesis }) {
  const chapters = thesis.chapters || []
  const done     = chapters.filter(c => c.status === 'LOCKED' || c.status === 'REVIEWED').length

  return (
    <div
      className="rounded-xl p-6"
      style={{ background: '#FFFFFF', border: '1px solid #DDE4EF' }}
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(37,99,235,0.1)' }}>
          <BookOpen style={{ width: 20, height: 20, color: '#2563eb' }} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-xl text-acdyon-text mb-1">{thesis.title}</h2>
          <p className="font-sans text-xs text-acdyon-textMuted">
            {thesis.university} · {thesis.programme}
          </p>
          <div className="flex items-center gap-3 mt-3">
            <span
              className="px-2 py-1 rounded font-sans text-xs"
              style={{ background: 'rgba(37,99,235,0.1)', color: '#2563eb' }}
            >
              {thesis.status?.replace('_', ' ')}
            </span>
            {chapters.length > 0 && (
              <span className="font-sans text-xs text-acdyon-textFaint">
                {done}/{chapters.length} chapters complete
              </span>
            )}
            {thesis.estimated_pages > 0 && (
              <span className="font-sans text-xs text-acdyon-textFaint">
                ~{thesis.estimated_pages} pages
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-3 mt-6">
        {thesis.toc_finalised ? (
          <Link
            to={`/app/thesis/${thesis.id}/chapters`}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-sans text-sm font-semibold transition-colors"
            style={{ background: '#2563eb', color: '#FFFFFF' }}
          >
            Continue Writing <ChevronRight style={{ width: 14, height: 14 }} />
          </Link>
        ) : (
          <Link
            to={`/app/thesis/${thesis.id}/toc`}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-sans text-sm font-semibold transition-colors"
            style={{ background: '#2563eb', color: '#FFFFFF' }}
          >
            {thesis.toc_data?.ai_generated ? 'Edit Table of Contents' : 'Build Table of Contents'}
            <ChevronRight style={{ width: 14, height: 14 }} />
          </Link>
        )}
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ThesisSetupPage() {
  const navigate = useNavigate()
  const { profile } = useAuthStore()

  const [existingThesis, setExistingThesis] = useState(null)
  const [checkingThesis, setCheckingThesis] = useState(true)
  const [showModal, setShowModal]           = useState(false)
  const [submitting, setSubmitting]         = useState(false)
  const [error, setError]                   = useState('')

  const [form, setForm] = useState({
    title:          '',
    university:     '',
    programme:      '',
    methodology:    'Mixed Methods',
    domain:         '',
    geographic_focus: '',
    researchTopic:  '',
    keywords:       [],
    objectives:     [''],
    questions:      [''],
    hypotheses:     [],
  })

  // Pre-fill from profile
  useEffect(() => {
    if (profile) {
      setForm(f => ({
        ...f,
        university:    profile.university    || '',
        programme:     profile.programme     || '',
        domain:        profile.domain        || '',
        keywords:      profile.keywords      || [],
        researchTopic: profile.research_topic || '',
      }))
    }
  }, [profile])

  // Check for existing thesis
  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get('/thesis')
        if (Array.isArray(data) && data.length > 0) setExistingThesis(data[0])
      } catch {
        // no thesis yet — show form
      } finally {
        setCheckingThesis(false)
      }
    }
    check()
  }, [])

  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.title.trim()) { setError('Please enter a thesis title.'); return }
    setError('')
    setSubmitting(true)
    try {
      const { data: thesis } = await api.post('/thesis', {
        title:            form.title,
        university:       form.university,
        programme:        form.programme,
        domain:           form.domain,
        methodology:      form.methodology,
        geographic_focus: form.geographic_focus || null,
        keywords:         form.keywords,
        objectives:       form.objectives.filter(Boolean),
        questions:        form.questions.filter(Boolean),
        hypotheses:       form.hypotheses.filter(Boolean),
      })
      navigate(`/app/thesis/${thesis.id}/toc`)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create thesis. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (checkingThesis) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
      </div>
    )
  }

  if (existingThesis) {
    return (
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="font-display text-acdyon-text" style={{ fontSize: '2rem', lineHeight: 1.2 }}>
            My Thesis
          </h1>
          <p className="font-sans text-sm text-acdyon-textMuted mt-1">
            Your thesis is in progress. Continue from where you left off.
          </p>
        </div>
        <ExistingThesisCard thesis={existingThesis} />
      </div>
    )
  }

  return (
    <>
      <TitleSuggestModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSelect={(t) => setForm(f => ({ ...f, title: t }))}
        formData={form}
      />

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-10">

        {/* Header */}
        <div>
          <h1 className="font-display text-acdyon-text" style={{ fontSize: '2rem', lineHeight: 1.2 }}>
            Set Up Your Thesis
          </h1>
          <p className="font-sans text-sm text-acdyon-textMuted mt-1">
            This information shapes your AI-generated Table of Contents and every chapter that follows.
            The more detail you provide, the better the output.
          </p>
        </div>

        {/* ── Section 1: Identity ────────────────────────────────────── */}
        <section className="space-y-5">
          <h2 className="font-sans text-xs font-semibold tracking-widest uppercase text-acdyon-textFaint">
            Thesis Identity
          </h2>

          {/* Title */}
          <Field label="Thesis Title *">
            <div className="relative">
              <Input
                value={form.title}
                onChange={set('title')}
                placeholder="Enter your thesis title…"
              />
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-2.5 py-1 rounded-md font-sans text-xs font-medium transition-all"
                style={{ background: 'rgba(37,99,235,0.1)', color: '#2563eb', border: '1px solid rgba(37,99,235,0.2)' }}
              >
                <Sparkles style={{ width: 11, height: 11 }} /> AI Suggest
              </button>
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="University">
              <Input value={form.university} onChange={set('university')} placeholder="Your university" />
            </Field>
            <Field label="Programme">
              <Input value={form.programme} onChange={set('programme')} placeholder="e.g. PhD Management" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Research Domain">
              <Input value={form.domain} onChange={set('domain')} placeholder="e.g. Organisational Behaviour" />
            </Field>
            <Field label="Geographic / Industry Focus" hint="Optional">
              <Input value={form.geographic_focus} onChange={set('geographic_focus')} placeholder="e.g. Indian SMEs" />
            </Field>
          </div>
        </section>

        {/* ── Section 2: Methodology ─────────────────────────────────── */}
        <section className="space-y-4">
          <h2 className="font-sans text-xs font-semibold tracking-widest uppercase text-acdyon-textFaint">
            Research Methodology
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {METHODOLOGIES.map(({ value, label, icon: Icon, desc }) => {
              const active = form.methodology === value
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, methodology: value }))}
                  className="flex items-start gap-3 p-4 rounded-xl text-left transition-all"
                  style={{
                    background: active ? 'rgba(37,99,235,0.08)' : '#F4F7FC',
                    border: `1px solid ${active ? 'rgba(37,99,235,0.4)' : '#DDE4EF'}`,
                  }}
                >
                  <Icon style={{ width: 16, height: 16, color: active ? '#2563eb' : '#8EA4BC', marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <p className="font-sans text-sm font-medium" style={{ color: active ? '#2563eb' : '#0C1629' }}>{label}</p>
                    <p className="font-sans text-xs mt-0.5" style={{ color: active ? '#2563eb' : '#8EA4BC' }}>{desc}</p>
                  </div>
                </button>
              )
            })}
          </div>
        </section>

        {/* ── Section 3: Research Architecture ──────────────────────── */}
        <section className="space-y-6">
          <h2 className="font-sans text-xs font-semibold tracking-widest uppercase text-acdyon-textFaint">
            Research Architecture
          </h2>

          <DynamicList
            label="Research Objectives"
            hint="What does this thesis aim to achieve? (at least 3–5 objectives)"
            items={form.objectives}
            onChange={set('objectives')}
            placeholder="To investigate the impact of…"
          />

          <DynamicList
            label="Research Questions"
            hint="What specific questions will your thesis answer?"
            items={form.questions}
            onChange={set('questions')}
            placeholder="What is the relationship between…"
          />

          <DynamicList
            label="Hypotheses / Propositions"
            hint="Optional — state your propositions if applicable"
            items={form.hypotheses}
            onChange={set('hypotheses')}
            placeholder="H1: There is a significant positive relationship between…"
          />

          <KeywordInput keywords={form.keywords} onChange={set('keywords')} />
        </section>

        {/* ── Error + Submit ─────────────────────────────────────────── */}
        {error && (
          <div className="px-4 py-3 rounded-lg font-sans text-sm" style={{ background: 'rgba(239,68,68,0.1)', color: '#F87171', border: '1px solid rgba(239,68,68,0.2)' }}>
            {error}
          </div>
        )}

        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={submitting}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-sans font-semibold text-sm transition-all disabled:opacity-60"
            style={{ background: '#2563eb', color: '#FFFFFF' }}
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                Creating…
              </>
            ) : (
              <>
                Create Thesis & Build ToC
                <ChevronRight style={{ width: 16, height: 16 }} />
              </>
            )}
          </button>
          <p className="font-sans text-xs text-acdyon-textFaint">
            You can edit all details later
          </p>
        </div>

      </form>
    </>
  )
}
